import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebaseConfig';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Chip,
  Tooltip,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import EditParkingLotDialog from '../components/EditParkingLotDialog';

interface QueueLot {
  id: string;
  lotCode?: string;
  lotName: string;
  address: string;
  ratePeriod?: string;
  currencies?: Record<string, { rate?: number | null }>;
  totalSpots?: number;
  vendorId?: string;
  createdAt?: { seconds: number } | Date | null;
  rejectionReason?: string;
  rejectedAt?: { seconds: number } | Date | null;
  rejectedBy?: string;
}

interface VendorSummary {
  companyName: string;
  loginEmail?: string;
  nmiOnboardingStatus?: string;
  nmiSecurityKeyLast4?: string;
}

const NMI_READY_STATES = new Set(['active', 'enabled']);

type TabKey = 'pending' | 'rejected';

export default function PendingApprovals() {
  const [tab, setTab] = useState<TabKey>('pending');
  const [lots, setLots] = useState<QueueLot[]>([]);
  const [vendorsById, setVendorsById] = useState<Record<string, VendorSummary>>({});
  const [loading, setLoading] = useState(true);
  const [busyLotId, setBusyLotId] = useState<string | null>(null);
  const [busyVendorId, setBusyVendorId] = useState<string | null>(null);
  const [editingLot, setEditingLot] = useState<QueueLot | null>(null);
  // Rejected count is shown on the tab label so the admin sees at a
  // glance whether there's anything to follow up on, without switching tabs.
  const [rejectedCount, setRejectedCount] = useState(0);

  const fetchForStatus = async (status: 'pending_review' | 'rejected') => {
    const snap = await getDocs(
      query(collection(db, 'parkinglots'), where('status', '==', status))
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as QueueLot));
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [visibleLots, rejected] = await Promise.all([
        fetchForStatus(tab === 'pending' ? 'pending_review' : 'rejected'),
        // Keep the rejected count current regardless of which tab is
        // active so the badge stays accurate.
        tab === 'rejected' ? Promise.resolve(null) : fetchForStatus('rejected'),
      ]);
      setLots(visibleLots);
      if (tab === 'rejected') {
        setRejectedCount(visibleLots.length);
      } else if (rejected) {
        setRejectedCount(rejected.length);
      }

      // Vendor lookup for the visible rows.
      const vendorIds = Array.from(
        new Set(visibleLots.map((l) => l.vendorId).filter((v): v is string => !!v))
      );
      const vendorMap: Record<string, VendorSummary> = {};
      await Promise.all(
        vendorIds.map(async (vid) => {
          const vSnap = await getDocs(
            query(collection(db, 'vendors'), where('vendorId', '==', vid))
          );
          if (!vSnap.empty) {
            vendorMap[vid] = vSnap.docs[0].data() as VendorSummary;
          }
        })
      );
      setVendorsById(vendorMap);
    } catch (e) {
      console.error('Failed to load approval queue:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleApprove = async (lot: QueueLot) => {
    const vendor = lot.vendorId ? vendorsById[lot.vendorId] : null;
    const nmiReady =
      vendor && NMI_READY_STATES.has(vendor.nmiOnboardingStatus || '');
    const confirmMsg = nmiReady
      ? `Approve "${lot.lotName}"? It will become bookable immediately.`
      : `Approve "${lot.lotName}"?\n\nWARNING: This partner does not have NMI provisioned yet ` +
        `(status: ${vendor?.nmiOnboardingStatus || 'not_started'}). ` +
        'Customers will not be able to pay until NMI keys are entered ' +
        'on the partner record. Approve anyway?';
    if (!window.confirm(confirmMsg)) return;

    setBusyLotId(lot.id);
    try {
      await updateDoc(doc(db, 'parkinglots', lot.id), {
        status: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: auth.currentUser?.email || null,
        // Clear any stale rejection trail from a previous round.
        rejectionReason: null,
        rejectedAt: null,
        rejectedBy: null,
      });
      await fetchAll();
    } catch (e: any) {
      alert('Failed to approve: ' + (e?.message || 'unknown error'));
    } finally {
      setBusyLotId(null);
    }
  };

  const handleReject = async (lot: QueueLot) => {
    const reason = window.prompt(
      `Why is "${lot.lotName}" being rejected? This message will be emailed to the partner.`
    );
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Rejection requires a reason.');
      return;
    }

    setBusyLotId(lot.id);
    try {
      // Server-side path: updates the lot AND sends the rejection email
      // via Resend so the partner gets the reason and a path to resubmit.
      const fns = getFunctions();
      const rejectLot = httpsCallable(fns, 'rejectLot');
      const result = (await rejectLot({ lotId: lot.id, reason: reason.trim() })).data as {
        success: boolean;
        emailSent: boolean;
        emailInfo?: string;
      };
      if (result.success && !result.emailSent) {
        // Rejection went through but the email did not. Surface the
        // server-provided reason so the admin can follow up out-of-band
        // (and so we can diagnose without digging through function logs).
        alert(
          'Lot rejected, but the notification email could not be sent. ' +
            'Please contact the partner directly.\n\n' +
            'Reason: ' + (result.emailInfo || 'unknown')
        );
      }
      await fetchAll();
    } catch (e: any) {
      alert('Failed to reject: ' + (e?.message || 'unknown error'));
    } finally {
      setBusyLotId(null);
    }
  };

  const handleSendBackToPending = async (lot: QueueLot) => {
    if (
      !window.confirm(
        `Send "${lot.lotName}" back to Pending Approval?\n\n` +
          'This clears the rejection so the lot re-enters the review queue.'
      )
    ) {
      return;
    }
    setBusyLotId(lot.id);
    try {
      await updateDoc(doc(db, 'parkinglots', lot.id), {
        status: 'pending_review',
        rejectionReason: null,
        rejectedAt: null,
        rejectedBy: null,
        sentBackAt: serverTimestamp(),
        sentBackBy: auth.currentUser?.email || null,
      });
      await fetchAll();
    } catch (e: any) {
      alert('Failed to send back to pending: ' + (e?.message || 'unknown error'));
    } finally {
      setBusyLotId(null);
    }
  };

  const handleMarkNmiProvisioned = async (vendorId: string) => {
    if (
      !window.confirm(
        "Mark this partner's NMI gateway as active? Only do this once you've " +
          "entered their NMI security and tokenization keys on the partner's record."
      )
    ) {
      return;
    }
    setBusyVendorId(vendorId);
    try {
      const vSnap = await getDocs(
        query(collection(db, 'vendors'), where('vendorId', '==', vendorId))
      );
      if (vSnap.empty) {
        alert('Vendor record not found.');
        return;
      }
      await updateDoc(vSnap.docs[0].ref, {
        nmiOnboardingStatus: 'active',
      });
      await fetchAll();
    } catch (e: any) {
      alert('Failed to update partner: ' + (e?.message || 'unknown error'));
    } finally {
      setBusyVendorId(null);
    }
  };

  const formatPricing = (lot: QueueLot) => {
    if (lot.currencies && Object.keys(lot.currencies).length > 0) {
      return Object.entries(lot.currencies)
        .map(([code, p]) => `${code} ${p.rate ?? 'N/A'}/${lot.ratePeriod || 'hour'}`)
        .join(', ');
    }
    return 'N/A';
  };

  const formatTs = (ts: QueueLot['createdAt']) => {
    if (!ts) return '—';
    let d: Date;
    if (ts instanceof Date) d = ts;
    else if ('seconds' in ts) d = new Date(ts.seconds * 1000);
    else return '—';
    return d.toLocaleDateString();
  };

  const nmiChip = (vendor: VendorSummary | null) => {
    const s = vendor?.nmiOnboardingStatus || 'not_started';
    if (NMI_READY_STATES.has(s)) {
      return <Chip label="NMI Active" color="success" size="small" />;
    }
    if (s === 'in_progress') {
      return <Chip label="NMI In Progress" color="warning" size="small" />;
    }
    return <Chip label="NMI Not Started" color="default" size="small" />;
  };

  const lotNameLink = (lot: QueueLot) => (
    <Link
      component="button"
      underline="hover"
      onClick={() => setEditingLot(lot)}
      sx={{
        textAlign: 'left',
        fontWeight: 600,
        color: 'primary.main',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        p: 0,
        fontSize: 'inherit',
      }}
    >
      {lot.lotName}
    </Link>
  );

  const partnerCell = (lot: QueueLot, vendor: VendorSummary | null) => (
    <TableCell>
      <Box>
        <Typography variant="body2">
          {vendor?.companyName || lot.vendorId || 'Unassigned'}
        </Typography>
        {vendor?.loginEmail && (
          <Typography variant="caption" color="textSecondary">
            {vendor.loginEmail}
          </Typography>
        )}
      </Box>
    </TableCell>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 1 }}>
        Lot Approvals
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Click a lot name to review or edit its details (including payment gateway setup) before approving.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as TabKey)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="pending" label="Pending Review" />
        <Tab
          value="rejected"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>Rejected</span>
              {rejectedCount > 0 && (
                <Chip
                  label={rejectedCount}
                  size="small"
                  color="error"
                  sx={{ height: 18, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          }
        />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : lots.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
          <Typography color="textSecondary">
            {tab === 'pending'
              ? 'No parking lots are awaiting approval.'
              : 'No parking lots have been rejected.'}
          </Typography>
        </Paper>
      ) : tab === 'pending' ? (
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Lot Code</TableCell>
                  <TableCell>Lot Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>NMI</TableCell>
                  <TableCell>Pricing</TableCell>
                  <TableCell>Spots</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lots.map((lot) => {
                  const vendor = lot.vendorId ? vendorsById[lot.vendorId] || null : null;
                  const nmiReady = NMI_READY_STATES.has(vendor?.nmiOnboardingStatus || '');
                  const busy = busyLotId === lot.id;
                  return (
                    <TableRow key={lot.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {lot.lotCode || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{lotNameLink(lot)}</TableCell>
                      <TableCell>{lot.address}</TableCell>
                      {partnerCell(lot, vendor)}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          {nmiChip(vendor)}
                          {!nmiReady && lot.vendorId && (
                            <Tooltip title="Flip the partner's NMI status to active once you've entered their NMI keys on the partner record.">
                              <Button
                                size="small"
                                onClick={() => handleMarkNmiProvisioned(lot.vendorId!)}
                                disabled={busyVendorId === lot.vendorId}
                                sx={{ textTransform: 'none', minWidth: 0, p: 0 }}
                              >
                                {busyVendorId === lot.vendorId ? 'Saving…' : 'Mark Active'}
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {formatPricing(lot)}
                        </Typography>
                      </TableCell>
                      <TableCell>{lot.totalSpots ?? '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatTs(lot.createdAt)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            disabled={busy}
                            onClick={() => handleApprove(lot)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            disabled={busy}
                            onClick={() => handleReject(lot)}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        // Rejected tab
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Lot Code</TableCell>
                  <TableCell>Lot Name</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell>Rejection Reason</TableCell>
                  <TableCell>Rejected By</TableCell>
                  <TableCell>Rejected On</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lots.map((lot) => {
                  const vendor = lot.vendorId ? vendorsById[lot.vendorId] || null : null;
                  const busy = busyLotId === lot.id;
                  return (
                    <TableRow key={lot.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {lot.lotCode || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{lotNameLink(lot)}</TableCell>
                      {partnerCell(lot, vendor)}
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {lot.rejectionReason || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{lot.rejectedBy || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatTs(lot.rejectedAt)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Send back to Pending Review">
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<UndoIcon />}
                            disabled={busy}
                            onClick={() => handleSendBackToPending(lot)}
                          >
                            Send to Pending
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <EditParkingLotDialog
        open={editingLot !== null}
        onClose={() => setEditingLot(null)}
        lot={editingLot as any}
        adminMode
        onSuccess={() => {
          setEditingLot(null);
          fetchAll();
        }}
      />
    </Box>
  );
}
